import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Crown, ArrowRight, CheckCircle2 } from "lucide-react";
import { Logo, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

export default function ErpDemo() {
  const { t, lang, setLang } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    team_size: "", city: "", rbq_number: "", work_type: "", message: "", language: lang,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSending(true);
    try {
      await axios.post(`${API}/erp-leads`, { ...form, language: lang });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur lors de l'envoi. Veuillez réessayer.");
    } finally { setSending(false); }
  };

  const reset = () => {
    setForm({ name: "", company: "", email: "", phone: "", team_size: "", city: "", rbq_number: "", work_type: "", message: "", language: lang });
    setDone(false);
  };

  return (
    <div className="min-h-screen hero-grid-bg py-12 px-4 sm:px-6" style={{ background: "transparent" }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm" style={{ color: "rgba(245,245,245,0.6)" }} data-testid="erp-back">
            <ArrowLeft size={16} /> {t.erp_form.back}
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs px-3 py-1 rounded-full border border-white/10" data-testid="lang-toggle">
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <Logo size="sm" />
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="relative p-8 sm:p-12 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(192,0,0,0.12) 0%, rgba(11,11,11,0.85) 35%, rgba(11,11,11,0.85) 65%, rgba(212,175,55,0.12) 100%)",
            border: "1px solid rgba(212,175,55,0.35)",
            borderRadius: 28,
            boxShadow: "0 0 80px rgba(212,175,55,0.12)",
          }}>
          <div className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center"
               style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
            <Crown size={18} color="#D4AF37" />
          </div>

          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 glow-gold"
                   style={{ background: "rgba(74, 222, 128, 0.12)", border: "1px solid rgba(74, 222, 128, 0.4)" }}>
                <CheckCircle2 size={36} color="#4ADE80" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black mb-3" style={{ color: "#F5F5F5" }}>{t.erp_form.success_title}</h1>
              <p className="text-sm sm:text-base mb-8 max-w-md mx-auto" style={{ color: "rgba(245,245,245,0.7)" }}>{t.erp_form.success_msg}</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button className="btn-ghost text-sm" onClick={() => nav("/")} data-testid="erp-success-home">{t.erp_form.back}</button>
                <button className="btn-red text-sm" onClick={reset} data-testid="erp-success-another">{t.erp_form.another}</button>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] mb-5"
                   style={{ background: "rgba(192,0,0,0.18)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.4)" }}>
                <Building2 size={12} /> {t.pricing.erp_badge}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-3" style={{ color: "#F5F5F5" }}>
                {t.erp_form.title}
              </h1>
              <p className="text-sm mb-8" style={{ color: "rgba(245,245,245,0.65)" }}>{t.erp_form.sub}</p>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.name} *</label>
                    <input required className="input-dark mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="erp-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.company} *</label>
                    <input required className="input-dark mt-2" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="erp-company" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.email} *</label>
                    <input required type="email" className="input-dark mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="erp-email" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.phone}</label>
                    <input className="input-dark mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="erp-phone" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.team_size}</label>
                    <select className="input-dark mt-2" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} data-testid="erp-team-size">
                      <option value="">—</option>
                      {t.erp_form.team_options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.city}</label>
                    <input className="input-dark mt-2" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="erp-city" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.rbq}</label>
                    <input className="input-dark mt-2" value={form.rbq_number} onChange={(e) => setForm({ ...form, rbq_number: e.target.value })} data-testid="erp-rbq" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.work_type}</label>
                    <input className="input-dark mt-2" value={form.work_type} onChange={(e) => setForm({ ...form, work_type: e.target.value })} data-testid="erp-work-type" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.erp_form.message}</label>
                  <textarea className="input-dark mt-2" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="erp-message" />
                </div>

                {err && (
                  <div className="text-sm px-4 py-3 rounded-2xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }}>
                    {err}
                  </div>
                )}

                <button type="submit" disabled={sending} className="btn-red w-full sm:w-auto justify-center" data-testid="erp-submit">
                  {sending ? <><Spinner /> {t.erp_form.sending}</> : <>{t.erp_form.submit} <ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
